// Site traffic tracker — fires fire-and-forget pageview and event beacons
// against the backend so the admin Traffic dashboard + public visitor proof
// widget both have real numbers to show.
//
// Visitor identity: a long-lived (2y) cookie + a session id (sessionStorage)
// so refreshes by the same browser are deduped as one visitor.
//
// Google Ads attribution: gclid is captured from the landing URL and persisted
// for 90 days. Every subsequent pageview in that window is attributed to that
// click — matches Google Ads' own attribution model.
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const VISITOR_COOKIE = "llf_vid";
const GCLID_COOKIE = "llf_gclid";
const VISITOR_DAYS = 365 * 2;
const GCLID_DAYS = 90;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function readCookie(name) {
    const pairs = (document.cookie || "").split(";");
    for (const p of pairs) {
        const [k, ...rest] = p.trim().split("=");
        if (k === name) return decodeURIComponent(rest.join("="));
    }
    return null;
}

function writeCookie(name, value, days) {
    const exp = new Date(Date.now() + days * 86400_000).toUTCString();
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${exp}; path=/; SameSite=Lax${secure}`;
}

function randomId() {
    // Cryptographically strong if available, otherwise good-enough.
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return "v-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getOrCreateVisitorId() {
    let id = readCookie(VISITOR_COOKIE);
    if (!id) {
        id = randomId();
        writeCookie(VISITOR_COOKIE, id, VISITOR_DAYS);
    }
    return id;
}

function getOrCreateSessionId() {
    try {
        let s = sessionStorage.getItem("llf_sid");
        if (!s) {
            s = randomId();
            sessionStorage.setItem("llf_sid", s);
        }
        return s;
    } catch {
        return "";
    }
}

function captureLandingParams() {
    // Capture from current URL — fall back to persisted gclid cookie when no
    // fresh gclid is in the URL (Google Ads attribution window: 90 days).
    const url = new URL(window.location.href);
    const fresh = {};
    const gclid = url.searchParams.get("gclid") || "";
    if (gclid) {
        writeCookie(GCLID_COOKIE, gclid, GCLID_DAYS);
        fresh.gclid = gclid;
    } else {
        const stored = readCookie(GCLID_COOKIE);
        if (stored) fresh.gclid = stored;
    }
    for (const k of UTM_KEYS) {
        const v = url.searchParams.get(k);
        if (v) {
            fresh[k] = v;
            // Persist UTM for the same window so attribution survives nav.
            writeCookie(`llf_${k}`, v, GCLID_DAYS);
        } else {
            const stored = readCookie(`llf_${k}`);
            if (stored) fresh[k] = stored;
        }
    }
    return fresh;
}

let _lastPathSent = "";

export async function trackPageview(path) {
    try {
        if (!BACKEND) return;
        const visitor_id = getOrCreateVisitorId();
        const session_id = getOrCreateSessionId();
        const landing = captureLandingParams();
        // De-dupe within the same SPA navigation: don't re-send if path unchanged.
        const pathOnly = path || (window.location.pathname + window.location.search);
        if (pathOnly === _lastPathSent) return;
        _lastPathSent = pathOnly;
        await axios.post(`${BACKEND}/api/track/pageview`, {
            path: pathOnly,
            referrer: document.referrer || "",
            title: document.title || "",
            visitor_id,
            session_id,
            gclid: landing.gclid || "",
            utm_source: landing.utm_source || "",
            utm_medium: landing.utm_medium || "",
            utm_campaign: landing.utm_campaign || "",
            utm_content: landing.utm_content || "",
            utm_term: landing.utm_term || "",
            viewport_w: window.innerWidth,
            viewport_h: window.innerHeight,
            screen_w: window.screen?.width || null,
            screen_h: window.screen?.height || null,
        }, { timeout: 6000 });
    } catch {
        // Never let analytics break the app.
    }
}

export async function trackEvent(eventType, name, props = {}) {
    try {
        if (!BACKEND) return;
        const visitor_id = getOrCreateVisitorId();
        const session_id = getOrCreateSessionId();
        await axios.post(`${BACKEND}/api/track/event`, {
            event_type: eventType,
            name: (name || "").toString().slice(0, 120),
            path: window.location.pathname,
            visitor_id,
            session_id,
            props,
        }, { timeout: 6000 });
    } catch {
        // Swallow analytics failures.
    }
}

// Scroll-depth tracker — fires 25/50/75/100% once per page.
const _scrollSeen = new Set();
function _onScroll() {
    const h = document.documentElement;
    const total = (h.scrollHeight - h.clientHeight) || 1;
    const pct = Math.min(100, Math.round(((h.scrollTop || document.body.scrollTop) / total) * 100));
    for (const milestone of [25, 50, 75, 100]) {
        if (pct >= milestone) {
            const key = `${window.location.pathname}:${milestone}`;
            if (!_scrollSeen.has(key)) {
                _scrollSeen.add(key);
                trackEvent("scroll_depth", `${milestone}%`, { milestone, path: window.location.pathname });
            }
        }
    }
}

let _scrollAttached = false;
export function attachScrollDepth() {
    if (_scrollAttached) return;
    _scrollAttached = true;
    window.addEventListener("scroll", _onScroll, { passive: true });
}

export function resetScrollSeen() {
    _scrollSeen.clear();
}
